import { useState } from 'react';
import { Settings, HelpCircle, ChevronDown } from 'lucide-react';
import openHelp from '../../utils/openHelp';
import logoIcon from '../../assets/icons/MeinGruenraum_Logo.png';
import GardenSwitcher from './GardenSwitcher';
import GardenSwitcherSheet from './GardenSwitcherSheet';
import UserMenu from '../Auth/UserMenu';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import { useGardenSettings } from '../../context/GardenSettingsContext';
import { useDashboard } from '../../context/DashboardContext';

/**
 * DashboardHeader — 52px top bar for the Dashboard view.
 *
 * Layout:
 *   [Logo + Brand] | [GardenSwitcher] | [Settings] [Help] [Profile]
 *
 * Mobile (< 768px): garden name button opens GardenSwitcherSheet (bottom sheet).
 * Desktop (≥ 768px): GardenSwitcher dropdown as before.
 */
const DashboardHeader = () => {
  const { isMobile } = useBreakpoint();
  const { settings } = useGardenSettings();
  const { setSettingsOpen } = useDashboard();
  const currentName = settings?.name || 'Mein Garten';
  const [sheetOpen, setSheetOpen] = useState(false);

  return (
    <header
      className="flex items-center justify-between h-[52px] px-4 bg-white border-b border-gray-200 flex-shrink-0"
      role="banner"
    >
      {/* Left: Logo + Brand */}
      <div className="flex items-center gap-2">
        <img
          src={logoIcon}
          alt="Mein Grünraum Logo"
          className="h-7 w-7 object-contain"
        />
        <span className="text-sm font-semibold text-gray-900 hidden md:block">
          Mein Grünraum
        </span>
      </div>

      {/* Center: Garden Switcher */}
      <div className="flex-1 flex justify-center">
        {isMobile ? (
          <>
            <button
              type="button"
              onClick={() => setSheetOpen(true)}
              aria-haspopup="dialog"
              aria-expanded={sheetOpen}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <span className="max-w-[160px] truncate">{currentName}</span>
              <ChevronDown size={14} className="text-gray-400" />
            </button>
            <GardenSwitcherSheet
              isOpen={sheetOpen}
              onClose={() => setSheetOpen(false)}
            />
          </>
        ) : (
          <GardenSwitcher />
        )}
      </div>

      {/* Right: Icon Buttons */}
      <div className="flex items-center gap-1">
        <button
          type="button"
          aria-label="Einstellungen"
          onClick={() => setSettingsOpen(true)}
          className="hidden md:block p-2 text-gray-600 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <Settings size={24} />
        </button>
        {/* Help stays reachable on mobile (#489): the manual carries the
            rights-relevant Datenquellen-Attribution + Haftungshinweise, so it
            must not be desktop-only like the Settings placeholder above. */}
        <button
          type="button"
          aria-label="Hilfe"
          onClick={() => openHelp('dashboard')}
          className="p-2 text-gray-600 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <HelpCircle size={24} />
        </button>
        <UserMenu />
      </div>
    </header>
  );
};

export default DashboardHeader;
